import React from 'react';
import { InputNumber } from 'antd';
import { Controller, useFormContext } from 'react-hook-form';
import { CrossInCircleIcon } from '@components/common/Icons/CrossInCircleIcon';
import { TickInCircleIcon } from '@components/common/Icons/TickInCircleIcon';
import {
  buildInputNumberClassName,
  inputAdornmentClassName,
  inputHelperClassName,
  inputLabelClassName,
} from '../formControlStyles';
import { IFormNumberFieldProps } from './types';

function formatThousands(value: string | number | undefined) {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const [integerPart, decimalPart] = String(value).split('.');
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
}

function parseThousands(value: string | undefined) {
  return value?.replace(/,/g, '') ?? '';
}

interface NumberFieldControlProps {
  field: {
    value: string | number | null | undefined;
    onChange: (value: string | number) => void;
    onBlur: () => void;
    ref: (instance: unknown) => void;
  };
  fieldState: {
    isDirty: boolean;
    isTouched: boolean;
    error?: { message?: string };
  };
  textFieldProps: IFormNumberFieldProps['textFieldProps'];
  numericFormatProps: IFormNumberFieldProps['numericFormatProps'];
  className?: string;
  shouldShowAdornment?: boolean;
  name: string;
  inputRef?: React.Ref<unknown>;
}

function NumberFieldControl({
  field,
  fieldState,
  textFieldProps,
  numericFormatProps,
  className,
  shouldShowAdornment = false,
  name,
  inputRef,
}: NumberFieldControlProps) {
  const isInteracted = fieldState.isDirty || fieldState.isTouched || field.value;
  const isError = Boolean(fieldState.error);
  const svgEndAdornment = isInteracted ? isError ? <CrossInCircleIcon /> : <TickInCircleIcon /> : null;
  const { suffix, prefix, label, disabled, placeholder, InputProps } = textFieldProps ?? {};
  const mergedPrefix = prefix ?? InputProps?.startAdornment;
  const mergedSuffix = (
    <>
      {shouldShowAdornment && svgEndAdornment}
      {InputProps?.endAdornment}
      {suffix}
    </>
  );
  const inputId = typeof label === 'string' ? label.toLowerCase().replace(/[^a-z0-9]+/g, '-') : name;
  const hasValue = field.value !== undefined && field.value !== null && String(field.value).length > 0;
  const decimalScale = numericFormatProps?.decimalScale;
  const allowNegative = numericFormatProps?.allowNegative ?? true;
  const fixedDecimalScale = numericFormatProps?.fixedDecimalScale;

  return (
    <div className={className}>
      {label ? (
        <label htmlFor={inputId} className={inputLabelClassName}>
          {label}
        </label>
      ) : null}
      <InputNumber
        id={inputId}
        className={buildInputNumberClassName({
          hasError: isError,
          isDisabled: disabled,
          hasValue,
          className,
        })}
        value={field.value === '' || field.value === null ? undefined : field.value}
        disabled={disabled}
        status={isError ? 'error' : undefined}
        placeholder={placeholder ?? InputProps?.placeholder}
        prefix={mergedPrefix ? <span className={inputAdornmentClassName}>{mergedPrefix}</span> : undefined}
        suffix={<span className={inputAdornmentClassName}>{mergedSuffix}</span>}
        controls={false}
        stringMode={false}
        min={allowNegative ? undefined : 0}
        precision={fixedDecimalScale ? decimalScale : undefined}
        decimalSeparator={'.'}
        formatter={formatThousands}
        parser={parseThousands}
        onChange={nextValue => {
          field.onChange(nextValue === null ? '' : nextValue);
        }}
        onBlur={field.onBlur}
        ref={node => {
          if (typeof inputRef === 'function') {
            inputRef(node);
          } else if (inputRef) {
            (inputRef as React.MutableRefObject<unknown>).current = node;
          }
          field.ref(node);
        }}
      />
      {fieldState.error?.message ? <p className={inputHelperClassName}>{fieldState.error.message}</p> : null}
    </div>
  );
}

export const FormNumberField: React.FC<IFormNumberFieldProps> = React.forwardRef(
  (
    {
      defaultValue = '',
      name,
      rules,
      textFieldProps,
      numericFormatProps,
      className = 'w-full',
      shouldShowAdornment = false,
    },
    ref,
  ) => {
    const { control } = useFormContext();

    return (
      <Controller
        defaultValue={defaultValue}
        rules={rules}
        name={name}
        control={control}
        render={props => (
          <NumberFieldControl
            field={props.field}
            fieldState={props.fieldState}
            textFieldProps={textFieldProps}
            numericFormatProps={numericFormatProps}
            className={className}
            shouldShowAdornment={shouldShowAdornment}
            name={name}
            inputRef={ref}
          />
        )}
      />
    );
  },
);

FormNumberField.displayName = 'FormNumberField';
